import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { apiSlice } from '@/shared/store/slices/apiSlice';
import { authSlice } from '@/shared/store/slices/authSlice';
import { platformTenantsPageSlice } from '@/shared/store/slices/platformTenantsPageSlice';
import { platformLanguagesPageSlice } from '@/shared/store/slices/platformLanguagesPageSlice';
import { adminUsersPageSlice } from '@/shared/store/slices/adminUsersPageSlice';
import { adminClientsPageSlice } from '@/shared/store/slices/adminClientsPageSlice';
import { adminClientDetailPageSlice } from '@/shared/store/slices/adminClientDetailPageSlice';
import { adminProjectDetailPageSlice } from '@/shared/store/slices/adminProjectDetailPageSlice';
import { adminTeamsPageSlice } from '@/shared/store/slices/adminTeamsPageSlice';
import { adminTeamDetailPageSlice } from '@/shared/store/slices/adminTeamDetailPageSlice';
import { adminUserDetailPageSlice } from '@/shared/store/slices/adminUserDetailPageSlice';
import { platformTenantDetailPageSlice } from '@/shared/store/slices/platformTenantDetailPageSlice';
import { signinFormSlice } from '@/shared/store/slices/signinFormSlice';
import { platformSigninFormSlice } from '@/shared/store/slices/platformSigninFormSlice';
import { authIntendedDestinationSlice } from '@/shared/store/slices/authIntendedDestinationSlice';
import { timerPageSlice } from '@/shared/store/slices/timerPageSlice';
import { timerEntryPrefsSlice } from '@/shared/store/slices/timerEntryPrefsSlice';
import { weeklyPageSlice } from '@/shared/store/slices/weeklyPageSlice';
import { calendarPageSlice } from '@/shared/store/slices/calendarPageSlice';
import { reportsPageSlice } from '@/shared/store/slices/reportsPageSlice';
import { profilePageSlice } from '@/shared/store/slices/profilePageSlice';
import { adminAuditLogPageSlice } from '@/shared/store/slices/adminAuditLogPageSlice';
import { adminSettingsPageSlice } from '@/shared/store/slices/adminSettingsPageSlice';
import { platformTenantAuditLogPageSlice } from '@/shared/store/slices/platformTenantAuditLogPageSlice';
import { platformAuditLogPageSlice } from '@/shared/store/slices/platformAuditLogPageSlice';

const persistedAuthReducer = persistReducer(
  { key: 'auth', version: 1, storage, whitelist: ['user', 'roles', 'refreshCredential', 'accessToken', 'preferredLocale'] },
  authSlice.reducer,
);

// The only slice persisted besides `auth` (MNT-21). The paradigm keeps every other
// slice reload-volatile; this narrow exception — a remembered entry mode and last-used
// date, keyed to the signed-in principal — is authorised by the design decision
// `persist-timer-entry-preference.md` (see state-shape §1). It holds no domain data.
const persistedTimerEntryPrefsReducer = persistReducer(
  {
    key: 'timerEntryPrefs',
    version: 1,
    storage,
    whitelist: ['principalId', 'lastEntryMode', 'lastEntryDate', 'lastEntryDateRecordedOn'],
  },
  timerEntryPrefsSlice.reducer,
);

const rootReducer = combineReducers({
  [apiSlice.reducerPath]: apiSlice.reducer,
  auth: persistedAuthReducer,
  platformTenantsPage: platformTenantsPageSlice.reducer,
  platformLanguagesPage: platformLanguagesPageSlice.reducer,
  adminUsersPage: adminUsersPageSlice.reducer,
  adminClientsPage: adminClientsPageSlice.reducer,
  adminClientDetailPage: adminClientDetailPageSlice.reducer,
  adminProjectDetailPage: adminProjectDetailPageSlice.reducer,
  adminTeamsPage: adminTeamsPageSlice.reducer,
  adminTeamDetailPage: adminTeamDetailPageSlice.reducer,
  adminUserDetailPage: adminUserDetailPageSlice.reducer,
  platformTenantDetailPage: platformTenantDetailPageSlice.reducer,
  signinForm: signinFormSlice.reducer,
  platformSigninForm: platformSigninFormSlice.reducer,
  authIntendedDestination: authIntendedDestinationSlice.reducer,
  timerPage: timerPageSlice.reducer,
  timerEntryPrefs: persistedTimerEntryPrefsReducer,
  weeklyPage: weeklyPageSlice.reducer,
  calendarPage: calendarPageSlice.reducer,
  reportsPage: reportsPageSlice.reducer,
  profilePage: profilePageSlice.reducer,
  adminAuditLogPage: adminAuditLogPageSlice.reducer,
  adminSettingsPage: adminSettingsPageSlice.reducer,
  platformTenantAuditLogPage: platformTenantAuditLogPageSlice.reducer,
  platformAuditLogPage: platformAuditLogPageSlice.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(apiSlice.middleware),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
